import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { DjaySpecimen } from '../../../mascot';
import type { DjayEmotion, DjayVariant, DjayAccessory } from '../../../mascot';
import type { AchievementRarity } from '../../../mascot/achievements/achievementData';
import { useMotionBudget } from '../../../app/hooks/useMotionBudget';
interface RarityDjayWrapperProps {
  rarity: AchievementRarity;
  emotion: DjayEmotion;
  variant?: DjayVariant;
  accessory?: DjayAccessory;
  isUnlocked: boolean;
  animate: boolean;
}
// Compact cards always render Djay at the small size.
const BASE_SIZE = 'sm' as const;
/**
 * RarityDjayWrapper — wraps a Djay specimen in rarity-specific visual treatment.
 *
 * Intent B (specimen): the card shows the REWARD look, not the learner's
 * selected look. Using <DjaySpecimen> makes that deliberate difference explicit
 * (see MASCOT_SSOT_ARCHITECTURE_AUDIT_2026 §5.2). This renderer was lifted out
 * of the section god-file per §4.2 so the specimen-vs-identity intent stays
 * isolated and the section file no longer owns mascot-rendering logic.
 */
export function RarityDjayWrapper({
  rarity,
  emotion,
  variant = 'default',
  accessory = 'none',
  isUnlocked,
  animate
}: RarityDjayWrapperProps) {
  const {
    shouldAnimate
  } = useMotionBudget();
  // Honour prefers-reduced-motion: the Djay specimen itself only animates when
  // the caller wants it AND the motion budget allows it; the rarity rings/crown
  // collapse to static treatments below.
  const specimenAnimate = animate && shouldAnimate;
  if (!isUnlocked) {
    return <div className="relative" data-id="element-1816" data-mp="398exhdsfb21o">
        <div className="grayscale opacity-50" data-id="element-1817" data-mp="39a8791yc22dw">
          <DjaySpecimen look={{
          variant: 'default',
          accessory: 'none'
        }} size={BASE_SIZE} emotion={emotion} animate={false} data-id="element-1818" data-mp="3e804mbhqygph" />
        </div>
      </div>;
  }
  switch (rarity) {
    case 'legendary':
      return <div className="relative" data-id="element-1819" data-mp="2yhio4g48ytzo">
          {/* Pulsing glow ring — static under reduced motion */}
          {shouldAnimate ? <motion.div className="absolute -inset-2 rounded-full bg-gradient-to-r from-purple-400 via-fuchsia-400 to-pink-400 opacity-40 blur-sm" aria-hidden="true" animate={{
          opacity: [0.3, 0.6, 0.3],
          scale: [1, 1.1, 1]
        }} transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-1820" data-mp="2tkiho43hjp81" /> : <div className="absolute -inset-2 rounded-full bg-gradient-to-r from-purple-400 via-fuchsia-400 to-pink-400 opacity-35 blur-sm" aria-hidden="true" data-id="element-1821" data-mp="3r5ei3d2fm7sr" />}
          {/* Crown overlay — floats only when motion is allowed */}
          {shouldAnimate ? <motion.div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20" aria-hidden="true" animate={{
          y: [0, -3, 0],
          rotate: [0, 5, -5, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-1822" data-mp="1rv6dazehk9aq">
              <Star className="w-5 h-5 text-amber-400 fill-amber-400 drop-shadow" data-id="element-1823" data-mp="2j9rm7gjjvqy" />
            </motion.div> : <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20" aria-hidden="true" data-id="element-1824" data-mp="3kstspg3voxif">
              <Star className="w-5 h-5 text-amber-400 fill-amber-400 drop-shadow" data-id="element-1825" data-mp="3ast8zt23kfdp" />
            </div>}
          <div className="relative z-10" data-id="element-1826" data-mp="3fsfjhuqjjhit">
            <DjaySpecimen look={{
            variant,
            accessory
          }} size={BASE_SIZE} emotion={emotion} animate={specimenAnimate} data-id="element-1827" data-mp="3ftgww5v14g3p" />
          </div>
        </div>;
    case 'rare':
      return <div className="relative" data-id="element-1828" data-mp="3a11ops9apsbm">
          {/* Golden glow ring — static under reduced motion */}
          {shouldAnimate ? <motion.div className="absolute -inset-1.5 rounded-full bg-gradient-to-r from-amber-300 via-yellow-300 to-orange-300 opacity-50 blur-sm" aria-hidden="true" animate={{
          opacity: [0.4, 0.6, 0.4]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-1829" data-mp="311y7kttovmzr" /> : <div className="absolute -inset-1.5 rounded-full bg-gradient-to-r from-amber-300 via-yellow-300 to-orange-300 opacity-45 blur-sm" aria-hidden="true" data-id="element-1830" data-mp="2heqfxubjcl3q" />}
          <div className="relative z-10" data-id="element-1831" data-mp="28fmx8rga67pr">
            <DjaySpecimen look={{
            variant,
            accessory
          }} size={BASE_SIZE} emotion={emotion} animate={specimenAnimate} data-id="element-1832" data-mp="23efputi1vajm" />
          </div>
          {/* Star sparkle — spins only when motion is allowed */}
          {shouldAnimate ? <motion.div className="absolute -top-1 -right-1 z-20" aria-hidden="true" animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 180, 360]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-1833" data-mp="ssqbpm8kqvlr">
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" data-id="element-1834" data-mp="95agjlg6u0r7" />
            </motion.div> : <div className="absolute -top-1 -right-1 z-20" aria-hidden="true" data-id="element-1835" data-mp="dzoi65lg144b">
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" data-id="element-1836" data-mp="3dkk3f6p5mqqv" />
            </div>}
        </div>;
    case 'uncommon':
      return <div className="relative" data-id="element-1837" data-mp="orn95h5ez242">
          {/* Subtle glow ring */}
          <div className="absolute -inset-1 rounded-full bg-emerald-300/30 blur-sm" aria-hidden="true" data-id="element-1838" data-mp="opu0jwb8q7tf" />
          <div className="relative z-10" data-id="element-1839" data-mp="yi9iitmjeweb">
            <DjaySpecimen look={{
            variant,
            accessory
          }} size={BASE_SIZE} emotion={emotion} animate={specimenAnimate} data-id="element-1840" data-mp="yh6r8klayrxv" />
          </div>
        </div>;
    default:
      return <DjaySpecimen look={{
        variant,
        accessory
      }} size={BASE_SIZE} emotion={emotion} animate={specimenAnimate} data-id="element-1841" data-mp="w2ebku5b62h" />;
  }
}